
import { UserBadge, UserLevel } from '@/types/badge';

export const USER_LEVELS: UserLevel[] = [
  {
    level: 1,
    name: '新手租客',
    minPoints: 0,
    maxPoints: 99,
    color: 'bg-gray-500',
    benefits: ['基本評論功能']
  },
  {
    level: 2,
    name: '資深租客',
    minPoints: 100,
    maxPoints: 299,
    color: 'bg-blue-500',
    benefits: ['基本評論功能', '圖片上傳']
  },
  {
    level: 3,
    name: '專業租客',
    minPoints: 300,
    maxPoints: 599,
    color: 'bg-purple-500',
    benefits: ['基本評論功能', '圖片上傳', '優先顯示評論']
  },
  {
    level: 4,
    name: '租房達人',
    minPoints: 600,
    maxPoints: 999,
    color: 'bg-orange-500',
    benefits: ['基本評論功能', '圖片上傳', '優先顯示評論', '專屬勳章']
  },
  {
    level: 5,
    name: '租房專家',
    minPoints: 1000,
    maxPoints: Infinity,
    color: 'bg-yellow-500',
    benefits: ['基本評論功能', '圖片上傳', '優先顯示評論', '專屬勳章', '特殊權限']
  }
];

export const AVAILABLE_BADGES: UserBadge[] = [
  {
    id: 'first_review',
    name: '初次評論',
    description: '發表第一篇租房評論',
    icon: '🏠',
    requirement: 1,
    type: 'reviews',
    color: 'bg-green-500'
  },
  {
    id: 'active_reviewer',
    name: '活躍評論者',
    description: '發表5篇租房評論',
    icon: '✍️',
    requirement: 5,
    type: 'reviews',
    color: 'bg-blue-500'
  },
  {
    id: 'super_reviewer',
    name: '評論達人',
    description: '發表10篇租房評論',
    icon: '🌟',
    requirement: 10,
    type: 'reviews',
    color: 'bg-purple-500'
  },
  {
    id: 'review_master',
    name: '評論大師',
    description: '發表20篇租房評論',
    icon: '👑',
    requirement: 20,
    type: 'reviews',
    color: 'bg-yellow-500'
  },
  {
    id: 'points_collector',
    name: '積分收集者',
    description: '累積100積分',
    icon: '💎',
    requirement: 100,
    type: 'points',
    color: 'bg-indigo-500'
  },
  {
    id: 'points_master',
    name: '積分大師',
    description: '累積500積分',
    icon: '💰',
    requirement: 500,
    type: 'points',
    color: 'bg-orange-500'
  },
  {
    id: 'veteran_member',
    name: '資深會員',
    description: '註冊滿30天',
    icon: '🏆',
    requirement: 30,
    type: 'tenure',
    color: 'bg-red-500'
  }
];

export const getUserLevel = (points: number): UserLevel => {
  return USER_LEVELS.find(level => points >= level.minPoints && points <= level.maxPoints) || USER_LEVELS[0];
};

export const getEarnedBadges = (userStats: {
  reviewCount: number;
  totalPoints: number;
  memberDays: number;
}): UserBadge[] => {
  return AVAILABLE_BADGES.filter(badge => {
    switch (badge.type) {
      case 'reviews':
        return userStats.reviewCount >= badge.requirement;
      case 'points':
        return userStats.totalPoints >= badge.requirement;
      case 'tenure':
        return userStats.memberDays >= badge.requirement;
      default:
        return false;
    }
  });
};

export const getNextLevelProgress = (points: number): {
  currentLevel: UserLevel;
  nextLevel: UserLevel | null;
  progress: number;
} => {
  const currentLevel = getUserLevel(points);
  const currentLevelIndex = USER_LEVELS.findIndex(level => level.level === currentLevel.level);
  const nextLevel = currentLevelIndex < USER_LEVELS.length - 1 ? USER_LEVELS[currentLevelIndex + 1] : null;
  
  let progress = 0;
  if (nextLevel) {
    const currentLevelPoints = points - currentLevel.minPoints;
    const requiredPoints = nextLevel.minPoints - currentLevel.minPoints;
    progress = Math.round((currentLevelPoints / requiredPoints) * 100);
  }
  
  return {
    currentLevel,
    nextLevel,
    progress: Math.min(progress, 100)
  };
};
